import api from "./api";

/* ============================================================
 * Visitor management API client.
 * Mirrors the asset api: a thin wrapper over the shared `api`
 * axios instance (auth token attached by interceptor).
 * Backend router prefix: /visitors
 * ========================================================== */

export type VisitorStatus =
  | "INVITED"
  | "CHECKED_IN"
  | "CHECKED_OUT"
  | "CANCELLED"
  | "EXPIRED";

export type VisitorScope =
  | "today"
  | "upcoming"
  | "inside"
  | "history"
  | "all"
  | "mine";

export type CheckMethod = "QR" | "MANUAL";

/** What the scanner asked the backend to do. */
export type ScanAction = "AUTO" | "CHECK_IN" | "CHECK_OUT" | "VERIFY";

/**
 * Why a scan was refused (or "OK"). The backend decides these in
 * utils/visitor_qr.py::plan_scan; the UI only maps them to a tone and an
 * instruction, never re-derives them.
 */
export type ScanResult =
  | "OK"
  | "NOT_FOUND"
  | "NOT_YET_VALID"
  | "EXPIRED"
  | "CANCELLED"
  | "ALREADY_IN"
  | "ALREADY_OUT"
  | "NOT_IN";

export interface Visitor {
  id: string;
  visitor_name: string;
  visitor_phone: string | null;
  visitor_email: string | null;
  company_name: string | null;
  visitor_count: number;
  photo_url: string | null;
  id_proof_type: string | null;
  id_proof_number: string | null;

  purpose: string | null;
  notes: string | null;
  visit_date: string | null;      // "yyyy-MM-dd"
  expected_time: string | null;   // "HH:MM"

  host_empt_id: string | null;
  host_user_id: string | null;
  host_name: string | null;
  host_employeeID: string | null;
  host_department: string | null;

  status: VisitorStatus;
  valid_from: string | null;
  valid_until: string | null;

  checked_in_at: string | null;
  checked_in_by_name: string | null;
  check_in_method: CheckMethod | null;
  checked_out_at: string | null;
  checked_out_by_name: string | null;
  check_out_method: CheckMethod | null;

  cancelled_at: string | null;
  cancel_reason: string | null;

  created_by: string | null;
  created_by_name: string | null;
  created_at: string | null;
  updated_at: string | null;

  // Only present on the detail / create / pass responses — a list response
  // deliberately carries no working credentials.
  pass_code?: string | null;
  qr_payload?: string | null;
  qr_svg?: string | null;
  scan_logs?: VisitorScanLog[];
}

export interface VisitorScanLog {
  id: string;
  visitor_id: string | null;
  action: string;        // CHECK_IN | CHECK_OUT | VERIFY
  result: ScanResult;
  method: CheckMethod;
  detail: string | null;
  code_hint: string | null;
  scanned_by_name: string | null;
  scanned_at: string | null;
}

export interface VisitorSummary {
  today_total: number;
  /** Head count, not row count — a party of four on one pass is four people. */
  today_people: number;
  expected: number;
  inside_now: number;
  checked_out_today: number;
  cancelled_today: number;
  expired_today: number;
  upcoming: number;
}

export interface VisitorDashboard {
  summary: VisitorSummary;
  today: Visitor[];
  inside: Visitor[];
  upcoming: Visitor[];
  can_manage: boolean;
}

export interface VisitorCreatePayload {
  visitor_name: string;
  visitor_phone?: string | null;
  visitor_email?: string | null;
  company_name?: string | null;
  visitor_count?: number | null;
  photo_url?: string | null;
  id_proof_type?: string | null;
  id_proof_number?: string | null;
  purpose?: string | null;
  notes?: string | null;
  visit_date?: string | null;     // "yyyy-MM-dd"; omitted ⇒ today
  expected_time?: string | null;  // "HH:MM"
  host_empt_id?: string | null;   // omitted ⇒ the caller hosts it
  valid_hours_after?: number | null;
}

export type VisitorUpdatePayload = Partial<VisitorCreatePayload>;

export interface VisitorPass {
  visitor_id: string;
  visitor_name: string;
  host_name: string | null;
  visit_date: string | null;
  expected_time: string | null;
  purpose: string | null;
  status: VisitorStatus;
  pass_code: string | null;
  qr_payload: string;
  qr_svg: string;
  valid_until: string | null;
  usable: boolean;
}

export interface ScanOutcome {
  visitor: Visitor;
  /** The action performed (a successful scan) … */
  action?: "CHECK_IN" | "CHECK_OUT";
  /** … or what VERIFY says would happen next; null when nothing can. */
  next_action?: "CHECK_IN" | "CHECK_OUT" | null;
  result: ScanResult;
  message: string;
  ok: boolean;
}

// ------------------------------------------------------------- dashboard ----

export const getVisitorDashboard = async (): Promise<VisitorDashboard> => {
  const res = await api.get("/visitors/dashboard");
  return res.data;
};

// ------------------------------------------------------------------ list ----

export const getVisitors = async (
  params: {
    scope?: VisitorScope;
    status?: VisitorStatus | "";
    q?: string;
    host_empt_id?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
  } = {}
): Promise<{ visitors: Visitor[]; count: number; can_manage: boolean }> => {
  const res = await api.get("/visitors", {
    params: {
      scope: params.scope || "today",
      ...(params.status ? { status: params.status } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.host_empt_id ? { host_empt_id: params.host_empt_id } : {}),
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
      ...(params.limit ? { limit: params.limit } : {}),
    },
  });
  return res.data;
};

export const getVisitor = async (visitorId: string): Promise<Visitor> => {
  const res = await api.get(`/visitors/${visitorId}`);
  return res.data.visitor;
};

export const getVisitorPass = async (visitorId: string): Promise<VisitorPass> => {
  const res = await api.get(`/visitors/${visitorId}/pass`);
  return res.data;
};

export const getVisitPurposes = async (): Promise<string[]> => {
  const res = await api.get("/visitors/purposes");
  return res.data.purposes || [];
};

// --------------------------------------------------------------- mutate ----

export const createVisitor = async (
  payload: VisitorCreatePayload
): Promise<Visitor> => {
  const res = await api.post("/visitors", payload);
  return res.data.visitor;
};

export const updateVisitor = async (
  visitorId: string,
  payload: VisitorUpdatePayload
): Promise<Visitor> => {
  const res = await api.put(`/visitors/${visitorId}`, payload);
  return res.data.visitor;
};

export const cancelVisitor = async (
  visitorId: string,
  reason?: string
): Promise<Visitor> => {
  const res = await api.post(`/visitors/${visitorId}/cancel`, {
    reason: reason || null,
  });
  return res.data.visitor;
};

export const deleteVisitor = async (visitorId: string): Promise<void> => {
  await api.delete(`/visitors/${visitorId}`);
};

// ---------------------------------------------------------------- desk ----

/**
 * Present a pass at the desk. `code` is whatever came off the camera or the
 * keyboard — the full QR payload, a pass URL, a bare token, or a typed pass
 * code; the backend parses it.
 *
 * A refusal comes back as HTTP 409 with `detail: { result, message, visitor }`,
 * so callers should read `err.response.data.detail` rather than assuming a
 * string — see `scanOutcomeFromError`.
 */
export const scanVisitorPass = async (
  code: string,
  action: ScanAction = "AUTO",
  note?: string
): Promise<ScanOutcome> => {
  const res = await api.post("/visitors/scan", {
    code,
    action,
    note: note || null,
  });
  return res.data;
};

/** Pull the structured refusal out of a 409 from `scanVisitorPass`. */
export const scanOutcomeFromError = (
  e: any
): { result: ScanResult; message: string; visitor?: Visitor } | null => {
  const detail = e?.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail) && detail.result) {
    return {
      result: detail.result as ScanResult,
      message: detail.message || "That pass cannot be used.",
      visitor: detail.visitor,
    };
  }
  if (typeof detail === "string" && detail.trim()) {
    return { result: "NOT_FOUND", message: detail };
  }
  return null;
};

export const checkInVisitor = async (
  visitorId: string,
  note?: string
): Promise<Visitor> => {
  const res = await api.post(`/visitors/${visitorId}/check-in`, {
    note: note || null,
  });
  return res.data.visitor;
};

export const checkOutVisitor = async (
  visitorId: string,
  note?: string
): Promise<Visitor> => {
  const res = await api.post(`/visitors/${visitorId}/check-out`, {
    note: note || null,
  });
  return res.data.visitor;
};

// ---------------------------------------------------------- notifications ----
// Visitor arrival alerts ride the shared notification bell via a namespaced
// task_id "visitor:<visitor_id>" (mirrors the asset/expense feeds).

export const getVisitorNotifications = (limit = 50) =>
  api.get(`/visitors/notifications/unread?limit=${limit}`);

export const markVisitorNotificationRead = (id: string) =>
  api.post(`/visitors/notifications/${id}/read`);
